import React, {
  useContext,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import ProductDetail from "../ProductDetail";
import { Product } from "../../store/product/ProductTypes";
import ProductSwatches from "../ProductSwatches";
import AppState from "../../store/AppState";
// import Chip from '@mui/material/Chip';
import {
  makeSelectProductSwatches,
  selectProductPrice,
  selectProductLeadingColor,
  selectedProductSwatches,
  selectIsProductDetailRefereshing,
  selectProductAnalyticsData,
  selectIsProductColorDetailRefereshing,
} from "../../store/product/ProductSelectors";
import {
  isRefreshingAllProducts,
  selectContentSlots,
  selectEditSequenceList,
  selectLockedProductInCurrentCategory,
  selectPinnedProductIds,
  selectLockedProducts,
  selectProductListEditSequence,
  selectWatchedProductsList,
  selectUpdatedLocksInCategory,
  selectIsPreviewMode,
} from "../../store/product-list/ProductListSelectors";
import ProductInventoryTable from "../product-inventory-tile/ProductInventoryTable";
import ProductAttributeSelection from "../product-attribute-tile/ProductAttributeSelection";
import { selectOverlay } from "../../store/overlay/OverlaySelectors";
import AnalyticsOverlay from "../overlays/AnalyticsOverlay";
import PinnedProductOverlay from "../overlays/PinnedProductOverlay";
import ProductCategoryOverlay from "../overlays/ProductCategoryOverlay";
import {
  selectAccountFeatures,
  selectCallerAccountId,
  selectConfigValue,
  selectProductImagePrefix,
} from "../../store/app-config/AppConfigSelectors";
import {
  selectCurrentLocale,
  selectCurrentStoreId,
} from "../../store/store-list/StoreListSelectors";
import { PLATFORM_DEFAULTS } from "../../utils/DefaultConfigs";
import {
  calculateBadges,
  getContentSlotsBeforeTheCurrentPosition,
  handleProductSequencePositioning,
  isAbsolutePath,
  isProductInWatchlist,
} from "../../utils/ProductUtil";
import {
  resetAllProducAnalyticsView,
  updatedSelectedSwatches,
} from "../../store/product/ProductActions";
import { OVERLAY_TABS, SEQUENCE_MODE_TYPE } from "../../utils/Constants";
import Rating from "@mui/material/Rating";
import { selectCurrentCategory } from "../../store/category/CategorySelectors";
import {
  Box,
  CircularProgress,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { AdapterInfoObject } from "../../utils/AppConfigUtils";
import { selectAdapterInfo } from "../../store/adapter-config/AdapterConfigSelectors";
import { ViewContext } from "../../context/view-context";
import {
  useStyles,
  StyledProductTileContent,
  StyledLoadingContainer,
  ScrollableContainer,
  StyledContainer,
  StyledCircularDiv,
  StyledSwatchDiv,
  StyledLoadingScoreContainer,
  StyledSwatchDivWithBackground,
} from "./ProductTileContentStyles";
import swatchIcon from "../../assets/color-swatch-icon.svg";
import Popover from "@mui/material/Popover";
import { ColorData } from "store/color-management/ColorManagementTypes";
import TooltipComponent from "components/common/ToolTip";
import {
  selectClusterRefreshResp,
  selectIsClusterResponseFetched,
  selectIsProductRequestListFetched,
} from "store/ai-cluster/AiClusterSelectors";
import { SubstitutionIcon } from "assets";
import {
  BoostIcon,
  BuriedIcon,
  SMAIIcon,
  BoostGreyIcon,
  BuriedGreyIcon,
} from "assets";
import { SubstitutionManagementModalId } from "components/substitution-mangement-modal/SubstitutionManagement";
import { selectIsModalOpen } from "store/modal/ModalSelectors";
import {
  productBadgeId,
  selectProductBadgeList,
} from "store/product-badge/ProductSelectors";
import { CDN_WIDGET_URL, removeDotFromStart } from "utils/WidgetUtil";
import {
  setPinnedUnPinnedProducts,
  updateProductListInState,
  updateProductListSequenceMode,
} from "store/product-list/ProductListActions";
import { setModalState } from "store/modal/ModalActions";
import ProductBadgesOverlay from "components/overlays/ProductBadgesOverlay";
import { selectCurrentCatalogId } from "store/catalog/CatalogSelectors";
import { allDefined, isDefined } from "utils/ValidationUtils";
import { useProductImage } from "hooks/useProductImage";

interface Props {
  product: Product;
  rating?: number;
  reviewCount?: number;
  score?: number;
  rank?: string;
  index: number;
  sequence?: number;
  productView: boolean;
  isDragging: boolean;
  isRecentlyAdded?: boolean;
  pageStart?: number;
  searchText?: string;
  currentProductId?: string;
  /** Find-products modal: hide badges, analytics/alert overlays, AI row, internal swatches; optional image override. */
  findProductsModal?: boolean;
  thumbnailPathOverride?: string | null;
}

const ProductTileContent: React.FC<Props> = (props) => {
  const intl = useIntl();
  const updatedProducts = useSelector(selectEditSequenceList);

  const {
    index,
    rating,
    reviewCount,
    score,
    rank,
    product: {
      analyticsId,
      typeCode,
      code,
      productId,
      name,
      productScore,
      stock,
      colors,
      hasColors,
      skuCoverage,
      isPublished,
      isDraft = false,
      isArchived = false,
      isUnlisted = false,
    },
    sequence,
    productView,
    isDragging,
    isRecentlyAdded,
    pageStart,
    searchText,
    currentProductId,
    findProductsModal = false,
    thumbnailPathOverride,
  } = props;
  const currentProductFromUpdatedState = updatedProducts?.find(
    (productData) => productData?.productId === productId,
  );
  const [selectedThumbnailImage, setSelectedThumbnailImage] = useState<
    string | undefined
  >("");
  const dispatch = useDispatch();
  const theme = useTheme();
  const classes = useStyles(theme);
  const localeId = useSelector(selectCurrentLocale) || "";
  const currentCategoryId = useSelector(selectCurrentCategory) || "";
  const catalogId = useSelector(selectCurrentCatalogId) || "";
  const categoryId = useSelector(selectCurrentCategory) || "";
  const editSequenceProducts = useSelector(selectProductListEditSequence);
  const storeId = useSelector(selectCurrentStoreId) || "";
  const adapterInfo: AdapterInfoObject = useSelector(selectAdapterInfo);
  const ecomPlatformType = adapterInfo?.adapterId?.toUpperCase() ?? "";
  const platformDefaults = PLATFORM_DEFAULTS[ecomPlatformType];
  const [colorName, setColorName] = useState<String>("");
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );
  const handleClose = (event) => {
    event.stopPropagation();
    setAnchorEl(null);
  };
  const productImagePrefix = useSelector((state: AppState) =>
    selectProductImagePrefix(state, storeId),
  );
  const open = Boolean(anchorEl);
  const publishedColors = colors?.filter(({ isPublished }) => isPublished);
  const displaySequence =
    useSelector((state: AppState) =>
      selectConfigValue(state, "displaySequences"),
    ) === "true";
  const colorSequenceMode = useSelector((state: AppState) =>
    selectConfigValue(state, "colorSequenceMode"),
  );
  const defaultLocaleId = useSelector((state: AppState) =>
    selectConfigValue(state, "defaultLocaleId"),
  );
  const attrValSequenceLocaleSpecific =
    useSelector((state: AppState) =>
      selectConfigValue(state, "attrValSequenceLocaleSpecific"),
    ) === "true";
  const leadingColorEnabled =
    useSelector((state: AppState) =>
      selectConfigValue(state, "leadingColorEnabled"),
    ) === "true";
  const storeAndAccountInventory = useSelector((state: AppState) =>
    selectConfigValue(state, "inventoryThreshold"),
  );
  const inventoryThreshold = parseInt(
    storeAndAccountInventory !== ""
      ? storeAndAccountInventory
      : (platformDefaults?.inventoryThreshold ?? ""),
  );

  const substituteSimilarProduct =
    stock !== undefined ? stock === 0 || stock <= inventoryThreshold : false;
  const isClusterResponseFetched = useSelector(selectIsClusterResponseFetched);
  const hasValidPriceRange = allDefined(
    props.product?.lowPrice,
    props.product?.highPrice,
  );
  const hasLowPrice = isDefined(props.product?.lowPrice);

  let type = typeCode === "ProductBean" ? "Product" : typeCode;
  const view = useContext(ViewContext);
  const columns = view.styles.column;
  const changeStyles =
    columns === 6 ? { fontSize: "0.75rem" } : { fontSize: "inherit" };
  const productTypeStyles =
    columns === 6 || columns === 5
      ? {
          display: "block",
          fontSize: "0.75rem",
        }
      : { fontSize: "inherit" };
  const selectProductSwatches = useMemo(makeSelectProductSwatches, []);
  const swatches = useSelector((state: AppState) =>
    selectProductSwatches(
      state,
      productId,
      colorSequenceMode,
      attrValSequenceLocaleSpecific,
      localeId,
      defaultLocaleId,
    ),
  );

  const productPrice = useSelector((state: AppState) =>
    selectProductPrice(state, productId),
  );
  const leadingColors = useSelector((state: AppState) =>
    selectProductLeadingColor(state, productId),
  );

  const selectedOverlay = useSelector(selectOverlay);
  const selectedSwatches = useSelector(selectedProductSwatches);
  const isProductDetailRefreshing = useSelector((state: AppState) =>
    selectIsProductDetailRefereshing(state, productId),
  );
  const isProductColorDetailRefreshing = useSelector((state: AppState) =>
    selectIsProductColorDetailRefereshing(state, productId),
  );
  const showProductAnalytics = useSelector((state: AppState) =>
    selectProductAnalyticsData(state, analyticsId),
  )?.showAnalytics;
  const loadSingleProductAnalytics = useSelector((state: AppState) =>
    selectProductAnalyticsData(state, analyticsId),
  )?.loadAnalytics;
  const watchedProductsList = useSelector(selectWatchedProductsList);
  const isCurrentProductInWatchlist = isProductInWatchlist(
    watchedProductsList,
    productId,
  );
  const isRefreshingEveryProduct = useSelector(isRefreshingAllProducts);
  const isBoostBuryEnabled = useSelector((state: AppState) =>
    selectConfigValue(state, "enableBoostBury"),
  );
  const fetchBoostBuryActionEnabled = useSelector((state: AppState) =>
    selectConfigValue(state, "enableBoostBuryAction"),
  );
  const isBoostBuryActionEnabled = fetchBoostBuryActionEnabled === "true";
  const isPreviewMode = useSelector(selectIsPreviewMode);
  const selectedSwatch = selectedSwatches[productId]?.selectedSwatch ?? "";
  const showBoostBury =
    isBoostBuryEnabled === "true" && selectedOverlay === OVERLAY_TABS.SMAI;
  const isSMAIOverlay = selectedOverlay === OVERLAY_TABS.SMAI;
  const isSubstitutionModalOpen = useSelector((state: AppState) =>
    selectIsModalOpen(state, SubstitutionManagementModalId),
  );
  const showSubstitutionLoading =
    substituteSimilarProduct &&
    isBoostBuryEnabled !== "true" &&
    selectedOverlay === OVERLAY_TABS.SMAI &&
    !isClusterResponseFetched;

  let price = productPrice ? productPrice.price.toString() : "N/A";
  if (productPrice && productPrice.lowPrice < productPrice.highPrice) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    price = productPrice.lowPrice + "-" + productPrice.highPrice;
  }
  const productData = props.product;
  const isUnmanagedProductsSupported = adapterInfo?.capabilities?.includes(
    "unmanaged-products",
  ) as boolean;

  const updatedLockedProductsInCategory = useSelector((state: AppState) =>
    selectUpdatedLocksInCategory(state, categoryId, catalogId),
  );

  const isSubstitutionEnabled = useSelector((state: AppState) =>
    selectConfigValue(state, "enableSubstitution"),
  );
  const productPosition = editSequenceProducts?.find(
    (prod) => prod.productId === productId,
  )?.position;

  const isAiOverlay = selectedOverlay === OVERLAY_TABS.SMAI;
  const isLowstock =
    stock !== undefined ? stock === 0 || stock <= inventoryThreshold : false;

  const showSubstituion =
    isClusterResponseFetched &&
    isAiOverlay &&
    isLowstock &&
    isSubstitutionEnabled === "true";

  const inventory =
    stock !== undefined && stock >= 0
      ? intl.formatMessage({
          id: "productTileContent.inventoryText",
          defaultMessage: "INV",
        }) +
        " : " +
        stock
      : "";
  let colorIndex = -1;
  if (
    leadingColorEnabled &&
    leadingColors &&
    leadingColors.length > 0 &&
    swatches
  ) {
    colorIndex = swatches.findIndex(
      (swatch) => swatch.attributeValueId === leadingColors[0].attributeValueId,
    );
  }
  if (colorIndex < 0 && swatches) {
    colorIndex = swatches.findIndex((color) => color.isPublished);
  }

  const id = open ? productId : undefined;

  const productImage = useProductImage(
    productId,
    productData,
    currentCategoryId,
  );

  const productDetailImage =
    findProductsModal && thumbnailPathOverride?.trim()
      ? thumbnailPathOverride
      : selectedThumbnailImage
        ? selectedThumbnailImage
        : productImage;

  const getColorSwatchName = useMemo(() => {
    // Check if the color name exists in publishedColors
    const colorName = publishedColors?.find(
      (color) => color.thumbnail === productImage,
    )?.name;

    if (colorName) return colorName;

    // Function to extract color name from variation attributes
    const getColorFromAttributes = (variationAttributes) => {
      for (const attr of variationAttributes) {
        for (const attrValue of attr.values || []) {
          const matchingColor = publishedColors?.find(
            (color) => color.colorId === attrValue.value,
          )?.name;
          if (matchingColor) return matchingColor;
        }
      }
      return undefined;
    };

    // Process image groups if productData and imageGroups exist
    if (productData && productData.imageGroups) {
      for (const imageGroup of productData.imageGroups) {
        if (
          imageGroup?.images?.some((image) => image.thumbnail === productImage)
        ) {
          if (
            imageGroup.variationAttributes &&
            Array.isArray(imageGroup.variationAttributes) &&
            imageGroup.variationAttributes.length > 0
          ) {
            const imageColorName = getColorFromAttributes(
              imageGroup.variationAttributes,
            );
            if (imageColorName) return imageColorName;
          }
        }
      }
    }

    return undefined;
  }, [publishedColors, productData, productImage]);

  const isRatingVisible = rating !== -1 && reviewCount !== -1;
  const clusterRefreshed = useSelector(selectClusterRefreshResp);
  const isClusterFetched = useSelector(selectIsProductRequestListFetched);
  const isRefreshed =
    clusterRefreshed.length &&
    clusterRefreshed[0]?.response?.statusCode === 200;
  const pinnedProducts = useSelector(selectPinnedProductIds);
  const accountId = useSelector(selectCallerAccountId);
  const features = useSelector((state: AppState) =>
    selectAccountFeatures(state, accountId),
  );
  const contentSlots = useSelector(selectContentSlots);
  const contentSlotsBeforeTheCurrentPosition =
    getContentSlotsBeforeTheCurrentPosition(contentSlots, index + 1);
  const isLockedProductsFeatureEnabled = features?.["locked-products"] ?? false;
  const isPinnedProductsFeatureEnabled = features?.["pinned-products"] ?? false;
  const lockedProductInCurrentCategory = useSelector((state: AppState) =>
    selectLockedProductInCurrentCategory(
      state,
      currentCategoryId,
      catalogId,
      productId ?? "",
      index + 1 - contentSlotsBeforeTheCurrentPosition,
    ),
  );
  const lockedProducts = useSelector((state: AppState) =>
    selectLockedProducts(state, currentCategoryId, catalogId),
  );

  const isProductLockedInCurrentCategory = !isLockedProductsFeatureEnabled
    ? false
    : Boolean(lockedProductInCurrentCategory);

  const positionDisplay = isUnmanagedProductsSupported
    ? intl.formatMessage({
        id: "productTileContent.positionText",
        defaultMessage: "Pos",
      }) +
      ": " +
      (productPosition ?? "-")
    : productPosition !== undefined
      ? intl.formatMessage({
          id: "productTileContent.positionText",
          defaultMessage: "Pos",
        }) +
        ": " +
        productPosition
      : displaySequence
        ? intl.formatMessage({
            id: "productTileContent.positionText",
            defaultMessage: "Pos",
          }) +
          ": " +
          sequence
        : intl.formatMessage({
            id: "productTileContent.positionText",
            defaultMessage: "Pos",
          }) +
          ": " +
          (sequence &&
          currentProductFromUpdatedState?.isInitialPositionAvailable
            ? sequence
            : intl.formatMessage({
                id: "productTileContent.notSetText",
                defaultMessage: "Not set",
              }));

  const swatchClickHandler = useCallback(
    (image: string, attrValId: string) => {
      const swatchObj = {
        selectedSwatch: attrValId,
        productImage: image,
      };

      dispatch(
        updatedSelectedSwatches({
          swatch: swatchObj,
          productId,
        }),
      );
    },
    [dispatch, productId],
  );

  const openSubsitutionManagement = () => {
    dispatch(resetAllProducAnalyticsView());
    dispatch(setModalState(SubstitutionManagementModalId, true, { productId }));
  };

  const handleColorSwatchName = useCallback((event, swatchImage, colorName) => {
    event.stopPropagation();
    setSelectedThumbnailImage(swatchImage);
    setColorName(colorName);
  }, []);

  const handleMultipleColorSwatches = useCallback((event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }, []);

  const productIdsArray = editSequenceProducts.map((p) => p.productId);
  const handleBoostBury = (event, productId, isBury = false) => {
    event.stopPropagation();

    // Find the first unlocked position
    const firstUnlockedIndex = productIdsArray.findIndex(
      (id) => !lockedProducts.some((p) => p.productId === id),
    );

    // Find the last unlocked position
    let lastUnlockedIndex = -1;
    for (let i = productIdsArray.length - 1; i >= 0; i--) {
      if (!lockedProducts.some((p) => p.productId === productIdsArray[i])) {
        lastUnlockedIndex = i;
        break;
      }
    }

    // Return early if product is already at the first/last unlocked position
    if (
      (!isBury && productIdsArray[firstUnlockedIndex] === productId) ||
      (isBury && productIdsArray[lastUnlockedIndex] === productId)
    ) {
      return;
    }
    const positionedProductIds: string[] = [];
    // Create a map of original positions for locked products
    const lockedProductPositions = new Map();
    productIdsArray.forEach((id, index) => {
      if (lockedProducts.some((p) => p.productId === id)) {
        lockedProductPositions.set(id, index);
        positionedProductIds.push(id);
      }
    });

    // Remove the target product from array
    const unlockedProducts = productIdsArray.filter(
      (id) => id !== productId && !lockedProductPositions.has(id),
    );

    unlockedProducts?.forEach((id) => {
      if (
        editSequenceProducts?.some(
          (p) => p.productId === id && p.position !== undefined,
        )
      ) {
        positionedProductIds.push(id);
      }
    });

    // Create final array with correct length
    const updatedProductArray = new Array(productIdsArray.length).fill(null);

    // First, place all locked products in their original positions
    lockedProductPositions.forEach((position, id) => {
      updatedProductArray[position] = id;
    });

    // Add the target product at start/end of remaining products based on boost/bury
    const productsToPlace = isBury
      ? [...unlockedProducts, productId]
      : [productId, ...unlockedProducts];
    const isUnpositionedAreaExists = editSequenceProducts?.some(
      (p) => p.position === undefined,
    );
    // If the product is being boosted or if  there is no unpositioned area, add the product to the positioned product ids
    if (!isBury || (isBury && !isUnpositionedAreaExists)) {
      positionedProductIds.push(productId);
    }

    // Fill remaining positions with unlocked products
    let currentIndex = 0;
    productsToPlace.forEach((id) => {
      while (
        currentIndex < updatedProductArray.length &&
        updatedProductArray[currentIndex] !== null
      ) {
        currentIndex++;
      }
      if (currentIndex < updatedProductArray.length) {
        updatedProductArray[currentIndex] = id;
      }
    });
    if (isPinnedProductsFeatureEnabled && pinnedProducts.length > 0) {
      dispatch(
        setPinnedUnPinnedProducts({
          productId,
          isPinned:
            isBury && updatedProductArray.length !== pinnedProducts.length
              ? false
              : true,
          catalogId,
          categoryId,
        }),
      );
    }
    const productIdList = updatedProductArray.map((id) => ({ productId: id }));
    let updatedProductIdList = [...productIdList];
    if (isUnmanagedProductsSupported) {
      updatedProductIdList = handleProductSequencePositioning(
        productIdList,
        new Set(positionedProductIds),
        updatedLockedProductsInCategory,
        dispatch,
      );
    }
    const productPayload = {
      products: updatedProductIdList,
      isSaveAction: true,
    };
    dispatch(updateProductListInState(productPayload));
    dispatch(updateProductListSequenceMode(SEQUENCE_MODE_TYPE.EDIT));
  };

  const mainBadgesInfo = useSelector(selectProductBadgeList);
  const queryProductBadgeAssignments = useSelector(productBadgeId);
  const badgeId = calculateBadges(
    mainBadgesInfo,
    queryProductBadgeAssignments,
    productId,
  );
  const badgeLabel =
    mainBadgesInfo?.find((badge) => badge.badgeId === badgeId)?.label ?? null;

  const colorSwatches = (color: ColorData) => {
    const isColorSwatchSelected = color.name === colorName;
    let borderClass;
    if (color.stock === 0) {
      borderClass = classes.redBorder(isColorSwatchSelected);
    } else if (color.stock <= inventoryThreshold) {
      borderClass = classes.yellowBorder(isColorSwatchSelected);
    } else if (isColorSwatchSelected) {
      borderClass = classes.selectedBoxStyle;
    } else {
      borderClass = classes.defaultBorder;
    }

    const swatchClass = {
      ...classes.colorSwatchImageContainer,
      ...borderClass,
    };
    const productColorSwatch = color.swatchImage || color.thumbnail;
    const swatchImage = isAbsolutePath(productColorSwatch)
      ? productColorSwatch
      : productImagePrefix + productColorSwatch;

    // Check if we should use cssColor as background
    const shouldUseCssColorBackground = !color.swatchImage && color.cssColor;

    return (
      <Box style={classes.swatch}>
        <div
          key={color.code}
          onClick={(event) =>
            handleColorSwatchName(event, color.thumbnail, color.name)
          }
          title={color.name}
        >
          <div style={swatchClass}>
            {shouldUseCssColorBackground ? (
              <StyledSwatchDivWithBackground color={color?.cssColor ?? ""} />
            ) : (
              <img
                src={swatchImage}
                alt={color.code}
                style={classes.colorSwatchImage}
              />
            )}
          </div>
        </div>
      </Box>
    );
  };

  useEffect(() => {
    const colorName = getColorSwatchName;
    if (colorName) setColorName(colorName);
  }, [getColorSwatchName]);

  const renderColorSwatches = useMemo(() => {
    if (publishedColors && publishedColors.length > 0) {
      return (
        <ScrollableContainer>
          {publishedColors.map((color, index) => {
            //Rendering color swatches upto 4 for columns 5,6 & upto 6 for remaining columns
            if (
              color.thumbnail !== "" &&
              color.swatchImage !== "" &&
              ((index < 4 && (columns === 6 || columns === 5)) ||
                (index < 6 && columns !== 6 && columns !== 5))
            ) {
              return colorSwatches(color);
            } else {
              return null;
            }
          })}
          {/*New condition to display the swatch container button on columns 2 to 4 if there are more than 6 swatch images  
          and on columns 5,6 if there are more than 4 swatch images*/}
          {((publishedColors.length > 4 && (columns === 6 || columns === 5)) ||
            publishedColors.length > 6) && (
            <>
              <div
                id={id}
                aria-describedby={id}
                style={classes.swatchButton}
                onClick={(event) => handleMultipleColorSwatches(event)}
              >
                <img
                  src={`${CDN_WIDGET_URL}${removeDotFromStart(swatchIcon)}`}
                  alt="Swatch Icon"
                />
              </div>
              <Popover
                open={open}
                onClose={(event) => handleClose(event)}
                id={id}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "bottom",
                  horizontal: "center",
                }}
                anchorEl={anchorEl}
                sx={classes.popoverStyles}
              >
                <div style={classes.swatchBox}>
                  {publishedColors.map((color, index) => {
                    //Excluding initial 4 color swatches for columns 5,6 & upto 6 for remaining columns
                    if (
                      color.thumbnail !== "" &&
                      color.swatchImage !== "" &&
                      ((index > 3 && (columns === 6 || columns === 5)) ||
                        (index > 5 && columns !== 6 && columns !== 5))
                    ) {
                      return colorSwatches(color);
                    } else {
                      return null;
                    }
                  })}
                </div>
              </Popover>
            </>
          )}
        </ScrollableContainer>
      );
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    colors,
    inventoryThreshold,
    selectedThumbnailImage,
    open,
    anchorEl,
    publishedColors,
  ]);

  const isNameMatch = useMemo(() => {
    if (!searchText || !name) return false;
    return name.toLowerCase().includes(searchText.toLowerCase());
  }, [searchText, name]);

  const isCodeMatch = useMemo(() => {
    if (!searchText || !code) return false;
    return code.toLowerCase().includes(searchText.toLowerCase());
  }, [searchText, code]);

  const isCurrentMatch = useMemo(() => {
    if (!searchText || (!isNameMatch && !isCodeMatch)) return false;
    return productId === currentProductId;
  }, [searchText, isNameMatch, isCodeMatch, productId, currentProductId]);

  const getHighlightColor = (isFieldMatch: boolean) => {
    if (!searchText || !isFieldMatch) return "transparent";
    return isCurrentMatch
      ? theme.palette?.neutral?.darkOrange
      : theme.palette?.neutral?.lightYellow;
  };

  const isStockUnavailable = stock !== undefined && stock <= 0;
  const disableBoostIcon =
    isStockUnavailable || isProductLockedInCurrentCategory || isPreviewMode;
  const hideBuryAction = score === 0;

  return (
    <div data-product-id={productId}>
      {(!productView ||
        (selectedOverlay !== OVERLAY_TABS.INVENTORY_OVERLAY &&
          selectedOverlay !== OVERLAY_TABS.ATTRIBUTES_OVERLAY)) && (
        <StyledProductTileContent isSMAIOverlay={isSMAIOverlay}>
          <Box>
            <Box sx={classes.headerBox}>
              {!findProductsModal &&
              (isUnmanagedProductsSupported || productPosition !== undefined) ? (
                <Typography variant="position">{positionDisplay}</Typography>
              ) : null}
            </Box>
            <Box sx={classes.headerBox}>
              {type ? (
                <TooltipComponent
                  tooltipTitle={type}
                  placement="bottom"
                  interactive
                  enterDelay={500}
                >
                  <Typography
                    style={productTypeStyles}
                    sx={classes.productType}
                    variant="subHeader"
                  >
                    {type}
                  </Typography>
                </TooltipComponent>
              ) : null}
            </Box>
            <Box sx={classes.headerBox}>
              {code ? (
                <TooltipComponent
                  tooltipTitle={code}
                  interactive
                  enterDelay={500}
                >
                  <Typography
                    style={changeStyles}
                    sx={classes.productCode}
                    variant="subtitle2"
                  >
                    {isDraft ? (
                      <span style={classes.visibleIcon}>
                        <i
                          className="fa-solid fa-pencil"
                          style={classes.indicator}
                        ></i>
                      </span>
                    ) : isArchived ? (
                      <span style={classes.visibleIcon}>
                        <i
                          className="fa-solid fa-box-archive"
                          style={classes.indicator}
                        ></i>
                      </span>
                    ) : isUnlisted ? (
                      <span style={classes.visibleIcon}>
                        <i
                          className="fa-solid fa-link-slash"
                          style={classes.indicator}
                        ></i>
                      </span>
                    ) : !isPublished ? (
                      <span style={classes.visibleIcon}>
                        <i
                          className="fa-solid fa-eye-slash"
                          style={classes.indicator}
                        ></i>
                      </span>
                    ) : null}
                    <span
                      style={{
                        backgroundColor: getHighlightColor(isCodeMatch),
                      }}
                    >
                      {code}
                    </span>
                  </Typography>
                </TooltipComponent>
              ) : null}
            </Box>
            <Box sx={classes.headerBox}>
              {stock !== undefined ? (
                <Typography
                  sx={stock === 0 ? classes.inventoryColor : {}}
                  variant="inventory"
                >
                  {inventory}
                  {isCurrentProductInWatchlist && !findProductsModal && (
                    <span style={classes.bellIcon}>
                      <i className="fa-solid fa-bell" />
                    </span>
                  )}
                </Typography>
              ) : null}
              {showSubstituion && !findProductsModal && (
                <IconButton
                  size="medium"
                  color="inherit"
                  title={intl.formatMessage({
                    id: "productTile.substitutionIcon",
                    defaultMessage: "Substitutions",
                  })}
                  component="div"
                  aria-label="Substitute Similar Products"
                  onClick={openSubsitutionManagement}
                  sx={classes.substitutionIcon}
                >
                  <SubstitutionIcon width={31} height={18} />
                </IconButton>
              )}
            </Box>
            <Box sx={classes.headerBox}>
              {skuCoverage !== undefined ? (
                <Typography variant="coverage">
                  {intl.formatMessage({
                    id: "productDetail.skuCoverage",
                    defaultMessage: "SKU Coverage: ",
                  })}
                  {skuCoverage.toFixed(1)}
                </Typography>
              ) : null}
            </Box>
            <Box sx={classes.headerBox}>
              {!findProductsModal && productScore !== undefined ? (
                <>
                  <Typography variant="highlightedText" style={changeStyles}>
                    {intl.formatMessage({
                      id: "productDetail.productScore",
                      defaultMessage: "Product Score: ",
                    })}
                  </Typography>
                  <Typography variant="highlightedText" style={changeStyles}>
                    {Array.isArray(productScore)
                      ? `[${productScore.map((score) => Number(score).toFixed(2)).join(", ")}]`
                      : productScore.toFixed(2)}
                  </Typography>
                </>
              ) : null}
            </Box>
          </Box>
        </StyledProductTileContent>
      )}
      {!isDragging &&
        productView &&
        selectedOverlay !== OVERLAY_TABS.INVENTORY_OVERLAY &&
        selectedOverlay !== OVERLAY_TABS.ATTRIBUTES_OVERLAY &&
        selectedOverlay !== OVERLAY_TABS.CATEGORIES_OVERLAY && (
          <PinnedProductOverlay productId={productId} index={index} />
        )}
      {!findProductsModal &&
        !isSubstitutionModalOpen &&
        (productView || loadSingleProductAnalytics || showProductAnalytics) && (
          <AnalyticsOverlay
            productId={productId}
            showProductAnalytics={showProductAnalytics}
            loadSingleProductAnalytics={loadSingleProductAnalytics}
          />
        )}
      {productView && selectedOverlay === OVERLAY_TABS.CATEGORIES_OVERLAY && (
        <ProductCategoryOverlay productId={productId} />
      )}
      {productView && selectedOverlay === OVERLAY_TABS.PRODUCTS_BADGES && (
        <ProductBadgesOverlay productId={productId} />
      )}
      {!findProductsModal &&
      (isProductDetailRefreshing ||
        isProductColorDetailRefreshing ||
        isRefreshingEveryProduct) ? (
        <StyledContainer>
          <StyledCircularDiv>
            <CircularProgress />
          </StyledCircularDiv>
        </StyledContainer>
      ) : null}

      <ProductDetail
        name={name}
        price={
          props.product.listPrice !== undefined
            ? props.product.listPrice.toString()
            : ""
        }
        priceRange={
          hasValidPriceRange
            ? [Number(props.product.lowPrice), Number(props.product.highPrice)]
            : hasLowPrice
              ? [Number(props.product.lowPrice)]
              : null
        }
        type={props.product.typeCode}
        image={productDetailImage}
        isRecentlyAdded={isRecentlyAdded}
        badgeLabel={findProductsModal ? null : badgeLabel}
        productId={productId}
        sequence={(pageStart ?? 0) + index + 1}
        highlightColor={getHighlightColor(isNameMatch)}
        findProductsModal={findProductsModal}
      />
      {!findProductsModal && (showSubstitutionLoading || showBoostBury) ? (
        <Box sx={classes.aiOverlayContainer}>
          {showSubstitutionLoading ? (
            <StyledLoadingScoreContainer>
              Loading Substitution...
            </StyledLoadingScoreContainer>
          ) : isRefreshed && !isClusterFetched && score !== -1 ? (
            <Box sx={classes.aiOverlayRatingsContainer}>
              <div style={classes.scoreTextPadding}>
                <Typography variant="subHeader" sx={classes.substitutionText}>
                  <SMAIIcon />
                  AI Rating: {`${score ? (score * 100).toFixed(2) : 0}`}
                  {rank === "boosted" ? (
                    <IconButton
                      sx={classes.boostBuryIcon(isBoostBuryActionEnabled)}
                      title={intl.formatMessage({
                        id: "productTile.BoostIcon",
                        defaultMessage: "Boost",
                      })}
                      component="div"
                      onClick={(event) =>
                        isBoostBuryActionEnabled &&
                        handleBoostBury(event, productId)
                      }
                      disabled={disableBoostIcon}
                    >
                      {disableBoostIcon ? <BoostGreyIcon /> : <BoostIcon />}
                    </IconButton>
                  ) : rank === "buried" && !hideBuryAction ? (
                    <IconButton
                      sx={classes.boostBuryIcon(isBoostBuryActionEnabled)}
                      title={intl.formatMessage({
                        id: "productTile.BuryIcon",
                        defaultMessage: "Bury",
                      })}
                      component="div"
                      onClick={(event) =>
                        isBoostBuryActionEnabled &&
                        handleBoostBury(event, productId, true)
                      }
                      disabled={
                        isProductLockedInCurrentCategory || isPreviewMode
                      }
                    >
                      {isProductLockedInCurrentCategory || isPreviewMode ? (
                        <BuriedGreyIcon />
                      ) : (
                        <BuriedIcon />
                      )}
                    </IconButton>
                  ) : null}
                </Typography>
              </div>
            </Box>
          ) : (
            <StyledLoadingScoreContainer>
              Loading...
            </StyledLoadingScoreContainer>
          )}
        </Box>
      ) : null}
      {productView &&
      selectedOverlay === OVERLAY_TABS.INVENTORY_OVERLAY &&
      typeCode !== "ProductSet" &&
      typeCode !== "ProductBundle" ? (
        <ProductInventoryTable
          productId={productId}
          swatches={swatches ? swatches : []}
        />
      ) : productView &&
        selectedOverlay === OVERLAY_TABS.ATTRIBUTES_OVERLAY &&
        typeCode !== "ProductSet" &&
        typeCode !== "ProductBundle" ? (
        <ProductAttributeSelection productId={productId} />
      ) : (
        !findProductsModal &&
        !isSMAIOverlay &&
        swatches &&
        swatches.length > 0 &&
        typeCode !== "ProductSet" &&
        typeCode !== "ProductBundle" && (
          <ProductSwatches
            productId={productId}
            swatches={swatches}
            selectedSwatch={selectedSwatch}
            onSwatchClick={swatchClickHandler}
          />
        )
      )}

      {hasColors &&
      selectedOverlay !== "ratings" &&
      !isSMAIOverlay &&
      !findProductsModal ? (
        <StyledSwatchDiv>
          {publishedColors &&
            publishedColors.length > 0 &&
            colorName !== "" && (
              <Typography variant="body3">Color: {colorName}</Typography>
            )}
          {renderColorSwatches}
        </StyledSwatchDiv>
      ) : null}
      {selectedOverlay === "ratings" && !findProductsModal ? (
        isRatingVisible ? (
          <Box ml={1} sx={classes.ratingsContainer}>
            <div>
              <Rating
                name="read-only"
                value={rating}
                precision={0.5}
                readOnly
              />
            </div>
            <div style={classes.ratingTextPadding}>
              <Typography variant="inherit">
                {`${rating} (${reviewCount})`}
              </Typography>
            </div>
          </Box>
        ) : (
          <StyledLoadingContainer>Loading...</StyledLoadingContainer>
        )
      ) : null}
    </div>
  );
};

export default React.memo(ProductTileContent);
